import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, CheckCircle, Monitor } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import HeroBackdrop from '../../components/UI/HeroBackdrop';
import { heroImageFade, heroShell } from '../../content/heroThemes';
import CourseFee from '../../components/UI/CourseFee';
import { COURSE_IMAGES } from '../../config/courseImages';
import { HOME_CONTENT } from '../../content/homePage';
import { PROGRAM_SERIES } from '../../content/programSeries';
import { useAuth } from '../../context/AuthContext';
import { Course } from '../../types';
import { formatDate } from '../../utils/formatDate';
import { getUserProfileId } from '../../utils/userProfile';
import {
  fetchActiveCourses,
  fetchAppliedCourseIds,
  isFlagshipCourse,
  overviewExcerpt,
  programDetailPath,
} from '../../utils/courses';

const ProgramsList: React.FC = () => {
  const { isAuthenticated, user, token } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [appliedCourseIds, setAppliedCourseIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const list = await fetchActiveCourses();
        setCourses(list);
      } catch {
        setError('Unable to load programs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const profileId = getUserProfileId(user);
    if (!isAuthenticated || !profileId) {
      setAppliedCourseIds(new Set());
      return;
    }

    let cancelled = false;
    const loadApplied = async () => {
      try {
        const ids = await fetchAppliedCourseIds(profileId, token);
        if (!cancelled) {
          setAppliedCourseIds(ids);
        }
      } catch {
        if (!cancelled) {
          setAppliedCourseIds(new Set());
        }
      }
    };
    loadApplied();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id, token]);

  const hero = (
    <section className={heroShell('academy')}>
      <HeroBackdrop tone="academy" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[1.35rem] md:py-[1.575rem] lg:py-[1.8rem]">
        <div className="grid lg:grid-cols-2 gap-[1.125rem] lg:gap-[1.8rem] items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-200">
              {PROGRAM_SERIES.navPrograms}
            </p>
            <h1 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
              {HOME_CONTENT.tagline}
            </h1>
            <p className="mt-4 text-base sm:text-lg font-semibold text-white leading-snug">
              {PROGRAM_SERIES.programsHeroTitle}
            </p>
            <p className="mt-3 text-sm text-primary-100/90 leading-relaxed">
              {PROGRAM_SERIES.programsHeroIntro}
            </p>
          </div>

          <div className="relative">
            <div className="relative overflow-hidden rounded-xl shadow-2xl ring-1 ring-white/10">
              <img
                src={COURSE_IMAGES.programsHero}
                alt="Engineers collaborating in an AI-native training program"
                className="w-full h-[12.6rem] sm:h-[15.3rem] lg:h-[18rem] object-cover"
              />
              <div className={heroImageFade('academy')} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  if (loading) {
    return (
      <div>
        {hero}
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        {hero}
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {hero}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {courses.length === 0 ? (
          <p className="text-center text-gray-600">No programs are available at the moment.</p>
        ) : (
          <div className="space-y-5">
            {courses.map((course) => {
              const hasApplied = appliedCourseIds.has(course.id);
              return (
              <Card key={course.id} className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-xl font-semibold text-gray-900">
                        {course.display_Title || course.title}
                      </h2>
                      {isFlagshipCourse(course, courses) && (
                        <span className="rounded-full bg-primary-50 px-2 py-0.5 text-xs font-semibold text-primary-700 ring-1 ring-primary-200">
                          Flagship
                        </span>
                      )}
                      {hasApplied && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
                          <CheckCircle className="h-3 w-3" />
                          Applied
                        </span>
                      )}
                    </div>
                    {course.courseOverview && (
                      <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                        {overviewExcerpt(course.courseOverview)}
                      </p>
                    )}
                    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-600">
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="h-4 w-4 text-primary-600" />
                        Starts {formatDate(course.startDate)}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Monitor className="h-4 w-4 text-primary-600" />
                        {course.trainingMode}
                      </span>
            <span className="font-medium text-gray-900">
              Program fee{' '}
              <CourseFee fee={course.fee} discount={course.discount} />
            </span>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <Link to={hasApplied ? '/dashboard?tab=academy' : programDetailPath(course.id)}>
                      <Button variant={hasApplied ? 'outline' : 'primary'}>
                        {hasApplied ? 'View application' : 'View details'}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramsList;
